import * as React from "react"
import { Head, Link } from "@inertiajs/react"
import { ArrowDown, ArrowUp, ShieldAlert, Star, TrendingDown, TrendingUp } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { type BadgeTone, segmentMeta } from "@/lib/accountSignals"

type AlertBrief = { key: string; name: string; tone: string }

type PortfolioRow = {
  id: number
  name: string
  code: string | null
  is_key_account: boolean
  pic_name: string | null
  opportunity_total: number
  opportunity_count: number
  risk_total: number
  risk_count: number
  alert_count: number
  alerts: AlertBrief[]
  segment: string
}

type Summary = {
  total_accounts: number
  scored_accounts: number
  critical: number
  high_opportunity: number
  high_risk: number
  watch: number
  no_data: number
  accounts_with_alerts: number
  total_alerts: number
  alerts_by_key: { key: string; name: string; tone: string; count: number }[]
}

type Thresholds = { opportunity_median: number; risk_median: number }

type PageProps = {
  rows: PortfolioRow[]
  summary: Summary
  thresholds: Thresholds
}

const DESCRIPTION =
  "Pandangan portofolio level-C: seluruh akun diperingkat dan disegmentasi berdasarkan skor Peluang vs Risiko, dengan peringatan kombinasi aktif yang teragregasi."

type SortKey = "name" | "opportunity_total" | "risk_total" | "alert_count" | "segment"

const SEGMENT_RANK: Record<string, number> = {
  critical: 0,
  high_risk: 1,
  high_opportunity: 2,
  watch: 3,
  no_data: 4,
}

export default function Portfolio({ rows, summary, thresholds }: PageProps) {
  const [sortKey, setSortKey] = React.useState<SortKey>("risk_total")
  const [sortDir, setSortDir] = React.useState<"asc" | "desc">("desc")

  const sorted = React.useMemo(() => {
    const copy = [...rows]
    copy.sort((a, b) => {
      let cmp = 0
      if (sortKey === "name") cmp = a.name.localeCompare(b.name)
      else if (sortKey === "segment") cmp = (SEGMENT_RANK[a.segment] ?? 9) - (SEGMENT_RANK[b.segment] ?? 9)
      else cmp = (a[sortKey] as number) - (b[sortKey] as number)
      return sortDir === "asc" ? cmp : -cmp
    })
    return copy
  }, [rows, sortKey, sortDir])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir(key === "name" || key === "segment" ? "asc" : "desc")
    }
  }

  return (
    <>
      <Head title="Portfolio Akun">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Portfolio Akun" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <div className="mb-2 flex items-center gap-1.5 text-xs text-ink-muted">
          <span>Outlet</span>
          <span>/</span>
          <Link href="/admin/accounts">Account</Link>
          <span>/</span>
          <span className="text-ink-body font-medium">Portfolio</span>
        </div>

        <div className="border-b border-hairline pb-6">
          <h1>Portfolio Akun</h1>
          <p className="mt-1 text-sm text-ink-muted">{DESCRIPTION}</p>
        </div>

        {/* Summary stats */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <StatCard label="Total akun" value={summary.total_accounts} />
          <StatCard label="Punya sinyal" value={summary.scored_accounts} />
          <StatCard label="Kritis" value={summary.critical} tone="danger" />
          <StatCard label="Risiko tinggi" value={summary.high_risk} tone="danger" />
          <StatCard label="Peluang tinggi" value={summary.high_opportunity} tone="success" />
          <StatCard label="Peringatan aktif" value={summary.total_alerts} />
        </div>

        {/* Active alert breakdown */}
        {summary.alerts_by_key.length > 0 && (
          <div className="mt-6 rounded-lg border border-hairline bg-surface p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-ink-muted">
              <ShieldAlert className="h-4 w-4" />
              Peringatan kombinasi aktif ({summary.accounts_with_alerts} akun)
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {summary.alerts_by_key.map((a) => (
                <Badge key={a.key} tone={(a.tone as BadgeTone) ?? "neutral"}>
                  {a.name} · {a.count}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <p className="mt-4 text-xs text-ink-muted">
          Segmentasi memakai <strong>median split</strong> di antara akun yang punya sinyal — akun
          tergolong &ldquo;tinggi&rdquo; saat skornya di atas median (Peluang{" "}
          {thresholds.opportunity_median.toFixed(1)} · Risiko {thresholds.risk_median.toFixed(1)}).
        </p>

        {rows.length === 0 ? (
          <div className="mt-10 rounded-lg border border-hairline bg-surface p-10 text-center">
            <p className="text-ink-muted">Belum ada akun aktif untuk ditampilkan.</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="mt-6 hidden overflow-x-auto rounded-lg border border-hairline md:block">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left text-ink-muted">
                  <tr>
                    <SortHeader label="Akun" col="name" {...{ sortKey, sortDir, toggleSort }} />
                    <SortHeader label="Peluang" col="opportunity_total" align="right" {...{ sortKey, sortDir, toggleSort }} />
                    <SortHeader label="Risiko" col="risk_total" align="right" {...{ sortKey, sortDir, toggleSort }} />
                    <SortHeader label="Peringatan" col="alert_count" align="right" {...{ sortKey, sortDir, toggleSort }} />
                    <SortHeader label="Segmen" col="segment" {...{ sortKey, sortDir, toggleSort }} />
                    <th className="px-4 py-3 font-medium">PIC</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r) => {
                    const seg = segmentMeta(r.segment)
                    return (
                      <tr key={r.id} className="border-t border-hairline hover:bg-surface">
                        <td className="px-4 py-3">
                          <Link href={`/admin/accounts/${r.id}`} className="font-medium">
                            {r.name}
                          </Link>
                          {r.is_key_account && (
                            <Badge tone="accent" className="ml-2">
                              <Star className="mr-1 h-3 w-3" />KA
                            </Badge>
                          )}
                          {r.code && <div className="text-xs text-ink-muted">{r.code}</div>}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-ink-body">
                          <span className="inline-flex items-center gap-1">
                            <TrendingUp className="h-3.5 w-3.5 text-ink-muted" />
                            {r.opportunity_total.toFixed(1)}
                          </span>
                          <div className="text-xs text-ink-muted">{r.opportunity_count} sinyal</div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-ink-body">
                          <span className="inline-flex items-center gap-1">
                            <TrendingDown className="h-3.5 w-3.5 text-ink-muted" />
                            {r.risk_total.toFixed(1)}
                          </span>
                          <div className="text-xs text-ink-muted">{r.risk_count} sinyal</div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {r.alert_count > 0 ? (
                            <Badge tone="danger">{r.alert_count}</Badge>
                          ) : (
                            <span className="text-ink-muted">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge tone={seg.tone}>
                            {seg.symbol} {seg.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-ink-muted">{r.pic_name ?? "—"}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="mt-6 flex flex-col gap-3 md:hidden">
              {sorted.map((r) => {
                const seg = segmentMeta(r.segment)
                return (
                  <Link
                    key={r.id}
                    href={`/admin/accounts/${r.id}`}
                    className="rounded-lg border border-hairline bg-surface p-4"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-medium text-ink-body">{r.name}</div>
                        {r.pic_name && <div className="text-xs text-ink-muted">PIC: {r.pic_name}</div>}
                      </div>
                      <Badge tone={seg.tone}>
                        {seg.symbol} {seg.label}
                      </Badge>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-3 text-sm">
                      <span className="inline-flex items-center gap-1 text-ink-body">
                        <TrendingUp className="h-3.5 w-3.5 text-ink-muted" /> {r.opportunity_total.toFixed(1)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-ink-body">
                        <TrendingDown className="h-3.5 w-3.5 text-ink-muted" /> {r.risk_total.toFixed(1)}
                      </span>
                      {r.alert_count > 0 && <Badge tone="danger">{r.alert_count} peringatan</Badge>}
                      {r.is_key_account && <Badge tone="accent">KA</Badge>}
                    </div>
                  </Link>
                )
              })}
            </div>
          </>
        )}
      </AppShell>
    </>
  )
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone?: "danger" | "success"
}) {
  const valueColor =
    tone === "danger"
      ? "text-danger"
      : tone === "success"
        ? "text-green-600 dark:text-green-400"
        : "text-ink-body"
  return (
    <div className="rounded-lg border border-hairline bg-surface p-4">
      <div className={`text-2xl font-semibold tabular-nums ${valueColor}`}>{value}</div>
      <div className="mt-1 text-xs text-ink-muted">{label}</div>
    </div>
  )
}

function SortHeader({
  label,
  col,
  align,
  sortKey,
  sortDir,
  toggleSort,
}: {
  label: string
  col: SortKey
  align?: "right"
  sortKey: SortKey
  sortDir: "asc" | "desc"
  toggleSort: (key: SortKey) => void
}) {
  const active = sortKey === col
  return (
    <th className={`px-4 py-3 font-medium ${align === "right" ? "text-right" : ""}`}>
      <button
        type="button"
        onClick={() => toggleSort(col)}
        className={`inline-flex items-center gap-1 hover:text-ink-body ${align === "right" ? "flex-row-reverse" : ""}`}
      >
        {label}
        {active &&
          (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </th>
  )
}
